//#!/usr/bin/env node
/**
 * BTC Signer
 * - bip32 / Hierarchical Deterministic Wallets
 * - bip38 / Passphrase-protected private key
 * - bip39 / Mnemonic code for generating deterministic keys
 * - bip43 / Purpose field for deterministic Wallets
 * - bip44 / Multi-Account Hierarchy for Deterministic Wallets
 * - bip45-49 / P2SH Multi-sig Wallets / Multi-script hierarchy for multi-sig Walets
 * - bip63 / Stealth Addresses
 * - bip83-84-85-86-87-88
 * - bip93 / Codex32
 * - bip98 / Fast Merkle Trees
 * - bip173 / Base32 address format for native v0-16 witness outputs
 * - bip178 / WIF Private Key
 * - bip350 / Bech32m format for v1+ witness addresses * 
 * - bip388 / Wallet Policies for Descriptor Wallets
 */

import crypto from 'crypto'
import secp256k1 from 'secp256k1'

import { hmac } from '@noble/hashes/hmac'
import { sha256 } from '@noble/hashes/sha256';
import { sha512 } from '@noble/hashes/sha512';
import { ripemd160 } from '@noble/hashes/ripemd160';

import { HDKey } from "@scure/bip32";
import * as bip39 from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';

//import * as hkdf from '@noble/hashes/hkdf';

import { pbkdf2 } from '@noble/hashes/pbkdf2';

import { base58 } from '@scure/base';
import { bech32, bech32m } from 'bech32';

import QRCode from 'qrcode'

import API from './lib/Api.js'

function dec2hex(str){ // .toString(16) only works up to 2^53
    var dec = str.toString().split(''), sum = [], hex = [], i, s
    while(dec.length){
        s = 1 * dec.shift()
        for(i = 0; s || i < sum.length; i++){
            s += (sum[i] || 0) * 10
            sum[i] = s % 16
            s = (s - sum[i]) / 16
        }
    }
    while(sum.length){
        hex.push(sum.pop().toString(16))
    }
    return hex.join('')
}

export default class Wallet {
    constructor() {
        /**
         * - HD KeyPair
         * - BIP32-39-44-84
         * - P2PKH
         * - P2SH
         * - P2WKH
         * - P2WSH
         * - P2TR
         */
        this.entropy = null
        this.mnemonic = null
        this.privateKey = null
        this.publicKey = null
        this.address = null
        this.hd = null
    }

    async randomBytes(browser=true) {
        // 
        const P = Uint8Array.from(Buffer.from("FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFC2F"))
        const A = Uint8Array.from(Buffer.from("0000000000000000000000000000000000000000000000000000000000000000"))
        const B = Uint8Array.from(Buffer.from("0000000000000000000000000000000000000000000000000000000000000007"))
        const Gcomp = Uint8Array.from(Buffer.from("0279BE667EF9DCBBAC55A06295CE870B07029BFCDB2DCE28D959F2815B16F81798"))
        const Gext = Uint8Array.from(Buffer.from("0479BE667EF9DCBBAC55A06295CE870B07029BFCDB2DCE28D959F2815B16F81798483ADA7726A3C4655DA4FBFC0E1108A8FD17B448A68554199C47D08FFB10D4B8"))
        const H = Uint8Array.from(Buffer.from("01"))

        //const secp256k1 = Uint8Array.from(Buffer.from("FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141"))
        
        // NODEJS / BROWSER
        //secp256k1.contextRandomize(new Uint8Array(32))
        let rndBytes = browser ? window.crypto.getRandomValues(new Uint8Array(32)) : crypto.getRandomValues(new Uint8Array(32))

        const curve = BigInt(`0x${Buffer.from(rndBytes).toString('hex')}`) //Uint8Array.from(Buffer.from(rndBytes))
        const order = BigInt(`0x${Buffer.from("FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141")}`)

        const ok = curve < order //rndBytes < secp256k1
        
        while (!ok) {
            rndBytes = browser ? window.crypto.getRandomValues(new Uint8Array(32)) : crypto.getRandomValues(new Uint8Array(32))
        }
        
        //console.log("[order]: ", order)
        //console.log("[curve]: ", curve)

        console.log("[RND BigInt]: ", curve) //.toString(16)
        console.log(`[RND Bytes]: (secp256k1-${ok})`,Buffer.from(rndBytes).toString('hex'))        

        //console.log(dec2hex(""))
        return rndBytes
    }

    async importFromMnemonic(sentence) {
        this.mnemonic = sentence
        this.entropy = bip39.mnemonicToEntropy(sentence, wordlist);
        //console.log("[ENTROPY]: ", Buffer.from(this.entropy).toString('hex'))
        this.privateKey = Buffer.from(this.entropy)
        this.publicKey = secp256k1.privateKeyVerify(this.privateKey)?Buffer.from(secp256k1.publicKeyCreate(this.privateKey)):0
        this.bip32()
    }

    async exportToMnemonic() {
        this.mnemonic = bip39.entropyToMnemonic(this.privateKey, wordlist);
        //console.log("[MNEMONIC]: ", this.mnemonic)
        this.importFromMnemonic(this.mnemonic)
    }

    async importPrivateKey(key) {
        const pre = Uint8Array.from(Buffer.from("302e0201010420",'hex')) //302e0201010420
        const mid = Uint8Array.from(Buffer.from(key))
        const post = Uint8Array.from(Buffer.from("a00706052b8104000a",'hex')) //secp256k1 //a00706052b8104000a
        
        //console.log(`[PRIVKEY]: ${Buffer.from(mid).toString('hex')}`)
        
        //console.log("[DER]: ",Buffer.from([...pre,...mid,...post]).toString('hex'))
        
        this.privateKey = secp256k1.privateKeyVerify(key)?Buffer.from(key):0
        this.publicKey = secp256k1.privateKeyVerify(key)?Buffer.from(secp256k1.publicKeyCreate(key)):0
        //this.exportToMnemonic()
        //this.bip32()
    }

    bip32(path="m/44'/0'/0'/0", i=0) {

        // NETWORKS
        const pubMainnet = new Uint8Array([0x04,0x88,0xB2,0x1E])
        const privMainnet = new Uint8Array([0x04,0x88,0xAD,0xE4])

        const pubTestnet = new Uint8Array([0x04,0x35,0x87,0xCF])
        const privTestnet = new Uint8Array([0x04,0x35,0x83,0x94])

        //console.log("\n====================== BIP32 =======================\n")
        /**
         *  Generate a seed byte sequence S of a chosen length (between 128 and 512 bits; 256 bits is advised) from a (P)RNG.
            Calculate I = HMAC-SHA512(Key = "Bitcoin seed", Data = S)
            Split I into two 32-byte sequences, IL and IR.
            Use parse256(IL) as master secret key, and IR as master chain code.
            In case parse256(IL) is 0 or parse256(IL) ≥ n, the master key is invalid.

            4 byte: version bytes (mainnet: 0x0488B21E public, 0x0488ADE4 private; testnet: 0x043587CF public, 0x04358394 private)
            1 byte: depth: 0x00 for master nodes, 0x01 for level-1 derived keys, ....
            4 bytes: the fingerprint of the parent's key (0x00000000 if master key)
            4 bytes: child number. This is ser32(i) for i in xi = xpar/i, with xi the key being serialized. (0x00000000 if master key)
            32 bytes: the chain code
            33 bytes: the public key or private key data (serP(K) for public keys, 0x00 || ser256(k) for private keys)
            
            This 78 byte structure can be encoded like other Bitcoin data in Base58, by first adding 32 checksum bits (derived from the double SHA-256 checksum), and then converting to the Base58 representation. 
            This results in a Base58-encoded string of up to 112 characters. Because of the choice of the version bytes, the Base58 representation will start with "xprv" or "xpub" on mainnet, "tprv" or "tpub" on testnet.
            Note that the fingerprint of the parent only serves as a fast way to detect parent and child nodes in software, and software must be willing to deal with collisions. Internally, the full 160-bit identifier could be used.

            When importing a serialized extended public key, implementations must verify whether the X coordinate in the public key data corresponds to a point on the curve. If not, the extended public key is invalid.
        */

        const key = this.bip39("")

        const depth = new Uint8Array([0x00]) // Master Node

        // Master Key Generation
        const S = key //Buffer.concat([this.privateKey, pass]) // 
        //const S = Buffer.concat([this.privateKey, Buffer.from("")]) // bip39 (S + mnemonic) + passphrase?
        const curve = Buffer.from('Bitcoin seed') // 'Nist256p1 seed' | 'ed25519 seed'

        const I = hmac(sha512, curve, S) // Bitcoin seed | Curve Point + SEED

        const il = I.slice(0,32) // parent Master Secret Private Key m/ 256bits 32byte
        const ir = I.slice(32,64) // parent Master Chain Code c
        const ok = il < Uint8Array.from(Buffer.from("FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141"))
        //console.log("[secp256k1]: ",ok)
        //console.log("[iL]: ",Buffer.from(il).toString('hex'))
        //console.log("[iR]: ",Buffer.from(ir).toString('hex'))

        // [priv,chain]
        const parentPrivKey = il // m/
        const parentChainCode = ir // c

        // Parent Pub Key
        //const parentPubKeyExt = secp256k1.privateKeyVerify(parentPrivKey)?secp256k1.publicKeyCreate(parentPrivKey,false):0
        const parentPubKey = secp256k1.privateKeyVerify(parentPrivKey)?secp256k1.publicKeyCreate(parentPrivKey,true):0 

        //console.log("[Master Parent PrivKey]: ",Buffer.from(I).toString('hex'))
        //console.log("[Master Parent PubKey compressed]: ", Buffer.from(parentPubKey).toString('hex'))

        // <prefix> <depth> <parent fgpr> <chaincode> <key>
        const rawPrivKey = Buffer.concat([
            //Buffer.from([0x00]),
            Buffer.from(privMainnet), 
            Buffer.from(depth),
            Buffer.from([0x00,0x00,0x00,0x00]), //Buffer.from(ripemd160.create().update(Buffer.from([0x00,0x00,0x00,0x00])).digest().slice(0,4)), //parentPrivKey
            Buffer.from([0x00,0x00,0x00,0x00]), // child index number
            Buffer.from(parentChainCode),
            Buffer.from([0x00]), // or Buffer.from("")
            Buffer.from(parentPrivKey) // or parentPubKeyc
        ])
        const rawPubKey = Buffer.concat([
            //Buffer.from([0x00]),
            Buffer.from(pubMainnet), 
            Buffer.from(depth),
            Buffer.from([0x00,0x00,0x00,0x00]), //Buffer.from(ripemd160.create().update(Buffer.from([0x00,0x00,0x00,0x00])).digest().slice(0,4)), //parentPrivKey
            Buffer.from([0x00,0x00,0x00,0x00]), // child index number
            Buffer.from(parentChainCode),
            Buffer.from(parentPubKey) // or parentPubKeyc
        ])
        //console.log("[RAWKEY]: ", rawPrivKey.length, Buffer.from(rawPrivKey).toString('hex'))
        const cksmPriv = Buffer.from(sha256(sha256(rawPrivKey)).slice(0,4))
        const cksmPub = Buffer.from(sha256(sha256(rawPubKey)).slice(0,4))
        //console.log("[CKSM-priv]: ", Buffer.from(cksmPriv).toString('hex'))
        //console.log("[CKSM-pub]: ", Buffer.from(cksmPub).toString('hex'))

        const extPrivRaw = Buffer.concat([rawPrivKey, cksmPriv])
        const extPubRaw = Buffer.concat([rawPubKey, cksmPub])
        
        //base58
        /**
         * This 78 byte structure can be encoded like other Bitcoin data in Base58,
         * by first adding 32 checksum bits (derived from the double SHA-256 checksum), 
         * and then converting to the Base58 representation.
         * This results in a Base58-encoded string of up to 112 characters.
         */
        //console.log("[ * XPRIV-base58 root * ]: ", base58.encode(extPrivRaw)) // <--- BIP 32 Root Extendend Private Key
        //console.log("[ * XPUB-base58 root * ]: ", base58.encode(extPubRaw)) // <--- BIP 32 Root Extendend Public Key

        this.hd = HDKey.fromExtendedKey(base58.encode(extPrivRaw));
        this.hd = this.hd.derive(path)
        //console.log("[CKD-XPRIV]", path, this.hd.privateExtendedKey)
        //console.log("[CKD-XPUB]", path, this.hd.publicExtendedKey)

        this.hd = this.hd.deriveChild(i)

        this.privateKey = this.hd.privateKey
        this.publicKey = this.hd.publicKey
    }

    bip38() {
        /**
         * - base58check 6P...
         * - 0x0142 (non-EC-multiplied) + C0 = 6PR uncompressed | + E0 = 6PY compressed
         * - 0x0143 (EC-multiplied) + 00 = 6Pf uncompressed | + 20 = 6Pn compressed
         * - 37 bytes <flag 1 byte + 2xsha256(addr) + 16bytes +16bytes>
         * 
         * - Scrypt ( passphrase , salt=addresshash ) = 64bytes / 2
         * 
         *  NO FLAG
         *  Do AES256Encrypt(block = bitcoinprivkey[0...15] xor derivedhalf1[0...15], key = derivedhalf2), call the 16-byte result encryptedhalf1
         *  Do AES256Encrypt(block = bitcoinprivkey[16...31] xor derivedhalf1[16...31], key = derivedhalf2), call the 16-byte result encryptedhalf2
         *  The encrypted private key is the Base58Check-encoded concatenation of the following, which totals 39 bytes without Base58 checksum:
         *  0x01 0x42 + flagbyte + salt + encryptedhalf1 + encryptedhalf2
         */
        console.log("\n====================== BIP38 =======================\n")

        console.log("\n==================================================\n")
    }

    bip39(pass="") {
        /**
         * To create a binary seed from the mnemonic, 
         * we use the PBKDF2 function with a mnemonic sentence (in UTF-8 NFKD) used as the password 
         * and the string "mnemonic" + passphrase (again in UTF-8 NFKD) used as the salt. 
         * The iteration count is set to 2048 and HMAC-SHA512 is used as the pseudo-random function. 
         * The length of the derived key is 512 bits (= 64 bytes).
        */
        //console.log("\n====================== BIP39 =======================\n")
        const passphrase = pass
        const password = this.mnemonic
        const salt = "mnemonic" + passphrase

        const pbkey = pbkdf2(sha512, password, salt, { c: 2048, dkLen: 64 });

        //console.log("[BIP39-root seed]: ", Buffer.from(pbkey).toString('hex'))

        return pbkey
    }

    async getPublicKey() {
        //console.log("[PUBKEY]: ", this.publicKey.toString('hex'))
        return this.publicKey
    }

    async getAddress(type="p2sh",createQr=false,custom="") {
        /**
         * p2pkh
         * p2sh
         * p2wpkh
         * p2wsh
         * p2tr
         */
        //console.log("\n====================== ADDRESSES =======================\n")
        //console.log("[ADDRESS]: ")
        //console.log(sha256(this.publicKey))
        //console.log(Buffer.from(ripemd160.create().update(sha256(this.publicKey)).digest()).toString('hex'))
        // 00 p2pkh 05 p2sh 6f p2pkh testnet c4 p2sh testnet
        const p2pkh = 0x00 //Buffer.from([0x00])
        const p2sh = 0x05 //Buffer.from([0x05])
        const p2pkhtestnet = 0x6f //Buffer.from([0x6F])
        const p2shtestnet = 0xc4 //Buffer.from([0xC4])

        const pkh = sha256(this.publicKey)
        //console.log("[PKH]: ",Buffer.from(pkh).toString('hex'))
        const hash160 = ripemd160.create().update(pkh).digest()
        //console.log("[HASH160]",Buffer.from(hash160,'hex').toString('hex'))

        //***P2PKH***
        let sha = sha256(Buffer.concat([new Uint8Array([p2pkh]),hash160]))
        //console.log("[SHA*1]: ",Buffer.from(sha).toString('hex'))
        sha = sha256(sha)
        //console.log("[SHA*2]: ",Buffer.from(sha).toString('hex'))

        const cksm = sha.slice(0,4)
        //console.log("[cksm-p2pkh]: ",Buffer.from(cksm,'hex').toString('hex'))

        const address = Buffer.concat([new Uint8Array([p2pkh]),hash160,cksm])        
        //console.log("[P2PKH]: ",base58.encode(address))
        
        //***P2SH***
        const psh = sha256(Buffer.concat([new Uint8Array([0x00,0x14]),hash160]))
        //console.log("[PSH]: ",Buffer.from(psh).toString('hex'))
        const pshash160 = ripemd160.create().update(psh).digest()
        //console.log("[PSHASH160]",Buffer.from(pshash160,'hex').toString('hex'))

        sha = sha256(Buffer.concat([new Uint8Array([p2sh]),pshash160]))
        //console.log("[SHA*1]: ",Buffer.from(sha).toString('hex'))
        sha = sha256(sha)
        //console.log("[SHA*2]: ",Buffer.from(sha).toString('hex'))
        
        const p2shcksm = sha.slice(0,4)
        //console.log("[cksm-p2sh]: ",Buffer.from(p2shcksm,'hex').toString('hex'))

        const p2shaddress = Buffer.concat([new Uint8Array([p2sh]),pshash160,p2shcksm])
        //console.log("[P2SH]: ",base58.encode(p2shaddress))

        
        //***Testnet p2pkh***
        sha = sha256(Buffer.concat([new Uint8Array([p2pkhtestnet]),hash160]))
        //console.log("[SHA*1]: ",Buffer.from(sha).toString('hex'))
        sha = sha256(sha)
        //console.log("[SHA*2]: ",Buffer.from(sha).toString('hex'))

        const p2pkhtestcksm = sha.slice(0,4)
        //console.log("[cksm-test-p2pkh]: ",Buffer.from(p2pkhtestcksm,'hex').toString('hex'))

        const p2pkhtestaddress = Buffer.concat([new Uint8Array([p2pkhtestnet]), hash160, p2pkhtestcksm])
        //console.log("[P2PKHtestnet]",base58.encode(p2pkhtestaddress))
        
        //***Testnet p2sh***
        const pshtest = sha256(Buffer.concat([new Uint8Array([0x00,0x14]),hash160]))
        const pshtesthash160 = ripemd160.create().update(pshtest).digest()
        //console.log(Buffer.from(pshtesthash160).toString('hex'))

        sha = sha256(Buffer.concat([new Uint8Array([p2shtestnet]),pshtesthash160]))
        //console.log("[SHA*1]: ",Buffer.from(sha).toString('hex'))
        sha = sha256(sha)
        //console.log("[SHA*2]: ",Buffer.from(sha).toString('hex'))

        const p2shtestcksm = sha.slice(0,4)
        //console.log("[cksm-test-p2sh]: ",Buffer.from(p2shtestcksm,'hex').toString('hex'))

        const p2shtestaddress = Buffer.concat([new Uint8Array([p2shtestnet]), pshtesthash160, p2shtestcksm])
        //console.log("[P2SHtestnet]",base58.encode(p2shtestaddress))

        //***P2WPKH bech32 ***/ OP_DUP OP_HASH160 <pubKeyHash> OP_EQUALVERIFY OP_CHECKSIG
        
        //***P2WSH bech32 ***/ sha256 0 <scriptHash>
        
        //***P2TR bech32m***/ schnorr

        switch(type) {
            case "p2pkh":
                if (createQr) {
                    this.createQR("P2PKH Address",base58.encode(address))
                }
                return base58.encode(address)
            case "p2sh":
                if (createQr) {
                    this.createQR("P2SH Address",base58.encode(p2shaddress))
                }
                return base58.encode(p2shaddress)
            case "p2wpkh":
                const pkhash = ripemd160.create().update(sha256(this.publicKey)).digest()
                const pkwords = bech32.toWords(pkhash)
                pkwords.unshift(0x00)
                if (createQr) {
                    this.createQR("P2WPKH Address",bech32.encode("bc", pkwords))
                }
                return bech32.encode("bc", pkwords)
            case "p2wsh":
                //const shhash = sha256(this.publicKey)
                //const shhash = sha256(Buffer.concat([Uint8Array.from([0x21]),this.publicKey,Uint8Array.from([0xac])]))
                const shhash = sha256(Buffer.concat([Uint8Array.from([0x51,0x21]),this.publicKey,Uint8Array.from([0x51,0xae])]))
                const shwords = bech32.toWords(shhash)
                shwords.unshift(0x00)
                if (createQr) {
                    this.createQR("P2WSH Address",bech32.encode("bc", shwords))
                }
                return bech32.encode("bc", shwords)
            case "p2tr":
                const trhash = sha256(this.publicKey)
                const trwords = bech32m.toWords(trhash)
                trwords.unshift(0x01)
                if (createQr) {
                    this.createQR("P2TR Address",bech32m.encode("bc", trwords))
                }
                return bech32m.encode("bc",trwords)
            case "p2pkh-test":
                if (createQr) {
                    this.createQR("P2PKH-test Address",base58.encode(p2pkhtestaddress))
                }
                return base58.encode(p2pkhtestaddress)
            case "p2sh-test":
                if (createQr) {
                    this.createQR("P2SH-test Address",base58.encode(p2shtestaddress))
                }
                return base58.encode(p2shtestaddress)
            case "p2wpkh-test":
                const pkhashtest = ripemd160.create().update(sha256(this.publicKey)).digest()
                const pkwordstest = bech32.toWords(pkhashtest)
                pkwordstest.unshift(0x00)
                if (createQr) {
                    this.createQR("P2WPKH Test Address",bech32.encode("tb", pkwordstest))
                }
                return bech32.encode("tb", pkwordstest)
            case "p2wsh-test":
                //const shhashtest = sha256(this.publicKey)
                //const shhashtest = sha256(Buffer.concat([Uint8Array.from([0x21]),this.publicKey,Uint8Array.from([0xac])]))
                const shhashtest = sha256(Buffer.concat([Uint8Array.from([0x51,0x21]),this.publicKey,Uint8Array.from([0x51,0xae])]))
                const shwordstest = bech32.toWords(shhashtest)
                shwordstest.unshift(0x00)
                if (createQr) {
                    this.createQR("P2WSH Test Address",bech32.encode("tb", shwordstest))
                }
                return bech32.encode("tb", shwordstest)
            case "custom": // TODO: ??? P2SH-P2WSH
                const hash = this.sha256(custom)
                const words = bech32.toWords(hash)
                words.unshift(0x00)
                const p2sh = base58.encode(
                    Buffer.concat([
                        Uint8Array.from([0x05]),
                        this.hash160(Buffer.concat([Uint8Array.from([0x00,0x20]),custom])),
                        this.hash256(this.hash160(Buffer.concat([Uint8Array.from([0x00,0x20]),custom]))).slice(0,4)
                    ])                    
                )
                return bech32.encode("bc",words)
            case "custom-test": // TODO ???
                const hashtest = this.sha256(custom)
                const wordstest = bech32.toWords(hashtest)
                wordstest.unshift(0x00)
                const p2shtest = base58.encode(
                    Buffer.concat([
                        Uint8Array.from([0xc4]),
                        this.hash160(Buffer.concat([Uint8Array.from([0x00,0x20]),custom])),
                        this.hash256(this.hash160(Buffer.concat([Uint8Array.from([0x00,0x20]),custom]))).slice(0,4)
                    ])                    
                )
                return bech32.encode("tb",wordstest)
            default:
                console.log("no type address selected")
        }
        
    }

    async sign(data) {
        // sign the message
        const sigObj = secp256k1.ecdsaSign(data, this.privateKey)

        // verify the signature
        const isValid = secp256k1.ecdsaVerify(sigObj.signature, data, this.publicKey)
        //console.log("[SIGNATURE]: ", Buffer.from(sigObj.signature).toString('hex'), isValid)

        if (this.hd !== null) {
            //const hash = sha256(data)
            //console.log("[hash]: ", Buffer.from(hash).toString('hex'))
            const sig = this.hd.sign(data)
            //console.log("[bip32-sig]: ", Buffer.from(sig).toString('hex'))
            const valid = this.hd.verify(data,sig)
            //console.log("[Bip32-sig-valid]: ", valid)

            return sig
        }
        
        return Buffer.from(sigObj.signature)
    }

    async verify(sig,data,pubkey) {
        const isValid = secp256k1.ecdsaVerify(sig, data, pubkey)
        return isValid
    }

    sha256(data) {
        return sha256(data)
    }

    hash256(data) {
        return sha256(sha256(data))
    }

    hash160(data) {
        return ripemd160.create().update(sha256(data)).digest()
    }

    base58(data) {
        return base58.encode(data)
    }

    fromBase58(data) {
        return Buffer.from(base58.decode(data))
    }

    bech32(data,prefix,m=false) {
        return m ? bech32m.encode(prefix,bech32m.toWords(data)) : bech32.encode(prefix,bech32.toWords(data))
    }

    fromBech32(data) {
        const el = bech32.decode(data,data.len).words  //bech32.decode(address,62).words
        const decoded = Buffer.from(this.convertbits(el.slice(1),5,8,false))

        return Buffer.concat([Uint8Array.from([0x00]), Uint8Array.from([decoded.length]), decoded]) //m ? bech32m.decode(prefix,bech32m.toWords(data)) : bech32.decode(prefix,bech32.toWords(data))
    }

    convertbits(data, frombits, tobits, pad) {
        var acc = 0;
        var bits = 0;
        var ret = [];
        var maxv = (1 << tobits) - 1;
        for (var p = 0; p < data.length; ++p) {
            var value = data[p];
            if (value < 0 || (value >> frombits) !== 0) {
            return null;
            }
            acc = (acc << frombits) | value;
            bits += frombits;
            while (bits >= tobits) {
            bits -= tobits;
            ret.push((acc >> bits) & maxv);
            }
        }
        if (pad) {
            if (bits > 0) {
            ret.push((acc << (tobits - bits)) & maxv);
            }
        } else if (bits >= frombits || ((acc << (tobits - bits)) & maxv)) {
            return null;
        }
        return ret;
    }

    createQR(name,data,file=false) {
        console.log(`\n************************ ${name} ************************\n`)
        QRCode.toString(data, {type:'terminal'}, function (err, url) {
            console.log(url)
        })
        console.log("\n**************************************************************\n")
        if (file) {
            QRCode.toFile(
                `${name}.png`,
                [{ data: Buffer.from([253,254,255]), mode: 'byte' }],
                //...options...,
                //...callback...
                ()=>{console.log("qr filke created...")}
            )
        }
    }

    wif(data,format="p2pkh",network="mainnet") {
        /**
         * No suffix	P2PKH_UNCOMPRESSED	No	Uncompressed legacy public key. Unknown public key format
         * 0x01	P2PKH_COMPRESSED	Yes	Compressed legacy public key. Unknown public key format
         * 0x10	P2PKH	Yes	Compressed legacy public key. Legacy public key format (1...)
         * 0x11	P2WPKH	Yes	Bech32 format (native Segwit)
         * 0x12	P2WPKH_P2SH	Yes	Segwit nested in BIP16 P2SH (3...)
         */
        let suffix
        let version
        switch(format) {
            case "p2pkh":
                suffix = 0x10
                break
            case "p2wpkh":
                suffix = 0x11
                break
            case "p2wpkh-p2sh":
                suffix = 0x12
                break
            default:
                suffix = 0x01
        }
        switch(network) {
            case "mainnet":
                version = 0x80
                break
            case "testnet":
                version = 0xef
                break
        }
        //cksm = hash256(key)[0:4]
        const key = Buffer.concat([Uint8Array.from([version]),data])
        const cksm = (this.hash256(key)).slice(0,4)
        //console.log("[CKSM]: ",Buffer.from(cksm).toString('hex'))
        //key = key + cksm
        const uncompressed = Buffer.concat([key,cksm])
        const compressed = Buffer.concat([key,cksm,Uint8Array.from([suffix])])
        //WIFext = base58(key)
        const wifExt = base58.encode(uncompressed)
        //WIFcomp = base58(key + 0x01) 
        const wifComp = base58.encode(compressed)
        //console.log("[WIF uncompressed]: ", wifExt)
        //console.log("[WIF compressed]: ", wifComp)

        return wifComp //compressed
    }

    fromWif(data) {
        //decode from string
        let buf = base58.decode(data)
        buf = buf.slice(1,-5)
        this.importPrivateKey(buf)
        //console.log("[FROM-WIF]", Buffer.from(buf).toString('hex'))
    }
    
    async getApi(id,url) {
        const api = await new API(id,url).get()
        return api
    }

    async postApi(id,url,data) {
        const api = await new API(id,url).post(data)
        return api
    }
}

const main = async (type="p2sh",online=false,test=false) => {
    console.log("* * * BTC Signer * * *")
    const wallet = new Wallet()
    const rnd = await wallet.randomBytes()
    //console.log(rnd.buffer)

    try {
        await wallet.importPrivateKey(Buffer.from("F000000000000000000000000000000000000000000000000000000000000001","hex")) //Buffer.from("",'hex')
    } catch (error) {
        console.log(error)
    }
    const pubKey = await wallet.getPublicKey()
    const address = await wallet.getAddress(type) // true for QRcode export
    console.log(`[ADDRESS-${type}]: `,address)
    await wallet.sign("pubKey")

    if (online) {
        const testnet = test?"testnet/":""
        try {
            //balance
            const status = await wallet.getApi("status",`https://mempool.space/${testnet}api/address/${address}`)
            //txs
            const txs = await wallet.getApi("utxo", `https://mempool.space/${testnet}api/address/${address}/txs`)
            //utxo
            const utxo = await wallet.getApi("txs", `https://mempool.space/${testnet}api/address/${address}/utxo`)
            //block
            const block = await wallet.getApi("block", `https://mempool.space/${testnet}api/blocks/tip/height`)
            //price
            const price = await wallet.getApi("price", `https://mempool.space/api/v1/prices`)

            //
            //const broadcast = https://mempool.space/testnet/api/tx //POST
            /*
            console.log("[STATUS]: ", status)
            console.log("[UTXO]: ", utxo)
            console.log("[TXS]: ", txs)
            console.log("[BLOCK]: ", block)
            console.log("[PRICE]: ", price)
            */
        } catch (error) {
            console.log("[API] error: ",error)
        }
    }

    //console.log(wallet)
}

/*
(async()=>{await main("p2sh-test",true,true) // address type, online, testnet
.then(()=>process.exit(0))
.catch((error)=>{
    console.error(error)
    process.exit(1)
})})()
*/
